(() => {
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const trapFocus = (event, container) => {
    if (event.key !== 'Tab') return;
    const nodes = [...container.querySelectorAll(focusableSelector)].filter((node) => node.offsetParent !== null);
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const initHeader = (root = document) => {
    root.querySelectorAll('[data-header-section]:not([data-header-ready])').forEach((header) => {
      header.dataset.headerReady = 'true';
      const toggle = header.querySelector('[data-menu-toggle]');
      const nav = header.querySelector('[data-mobile-nav]');
      const closeButton = header.querySelector('[data-menu-close]');
      const overlay = header.querySelector('[data-nav-overlay]');
      if (!toggle || !nav) return;

      const close = (restoreFocus = true) => {
        nav.querySelectorAll('[data-guide-menu]').forEach(menu => { menu.open = false; });
        nav.classList.remove('is-open');
        overlay?.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('menu-open');
        if (restoreFocus) toggle.focus();
      };
      const open = () => {
        nav.classList.add('is-open');
        overlay?.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        document.body.classList.add('menu-open');
        (nav.querySelector('[data-menu-close]') || nav.querySelector(focusableSelector))?.focus();
      };

      toggle.addEventListener('click', () => (nav.classList.contains('is-open') ? close() : open()));
      closeButton?.addEventListener('click', () => close());
      overlay?.addEventListener('click', () => close());
      nav.addEventListener('click', (event) => {
        if (event.target.closest('a')) close(false);
      });
      nav.addEventListener('keydown', (event) => {
        const guideMenu = event.target.closest('[data-guide-menu][open]');
        if (event.key === 'Escape' && guideMenu) {
          event.preventDefault();
          guideMenu.open = false;
          guideMenu.querySelector('summary')?.focus();
        } else if (event.key === 'Escape' && nav.classList.contains('is-open')) close();
        else if (nav.classList.contains('is-open')) trapFocus(event, nav);
      });
      nav.addEventListener('focusout', (event) => {
        nav.querySelectorAll('[data-guide-menu][open]').forEach(menu => {
          if (event.relatedTarget && !menu.contains(event.relatedTarget)) menu.open = false;
        });
      });

      const desktopQuery = window.matchMedia('(min-width: 1101px)');
      const handleDesktopChange = (event) => {
        if (event.matches && nav.classList.contains('is-open')) close(false);
      };
      desktopQuery.addEventListener('change', handleDesktopChange);
    });
  };

  document.addEventListener('click', event => {
    document.querySelectorAll('[data-guide-menu][open]').forEach(menu => {
      if (!menu.contains(event.target)) menu.open = false;
    });
  });

  const safeStorage = {
    get(key) {
      try { return window.localStorage.getItem(key); } catch { return null; }
    },
    set(key, value) {
      try { window.localStorage.setItem(key, value); } catch { /* Storage can be unavailable in privacy mode. */ }
    },
  };

  const initNewsletter = (root = document) => {
    root.querySelectorAll('[data-newsletter-dialog]:not([data-dialog-ready])').forEach((dialog) => {
      dialog.dataset.dialogReady = 'true';
      const version = dialog.dataset.popupVersion || 'default';
      const storageKey = `bolingo-newsletter-${version}`;
      const delay = Math.max(0, Number(dialog.dataset.delayMs || 8000));
      const dismissDays = Math.max(1, Number(dialog.dataset.dismissDays || 14));
      const closeButton = dialog.querySelector('[data-dialog-close]');
      const hasFormFeedback = Boolean(dialog.querySelector('[data-newsletter-success], [data-newsletter-error]'));
      let returnTarget = null;
      let timer;

      const isDismissed = () => {
        const value = Number(safeStorage.get(storageKey));
        return Number.isFinite(value) && value > Date.now();
      };
      const rememberDismissal = () => {
        safeStorage.set(storageKey, String(Date.now() + dismissDays * 86400000));
      };
      const open = () => {
        if (dialog.open) return;
        returnTarget = document.activeElement;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        dialog.querySelector('input[type="email"], button')?.focus();
      };
      const close = (remember = true) => {
        window.clearTimeout(timer);
        if (remember) rememberDismissal();
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
        if (returnTarget instanceof HTMLElement) returnTarget.focus();
      };

      closeButton?.addEventListener('click', () => close());
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        close();
      });
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) close();
      });
      dialog.addEventListener('keydown', (event) => trapFocus(event, dialog));

      if (hasFormFeedback) open();
      else if (!window.Shopify?.designMode && !isDismissed()) timer = window.setTimeout(open, delay);

      dialog.addEventListener('shopify:block:select', open);
    });
  };

  const syncProductRestock = (section, variant) => {
    const restock = section.querySelector('[data-product-restock]');
    if (!restock) return;
    const soldOut = Boolean(variant) && variant.available === false;
    const feedback = restock.querySelector('[data-restock-feedback]');
    restock.hidden = !soldOut && !feedback;
    const fields = restock.querySelector('fieldset');
    if (fields) fields.disabled = !soldOut;
    const details = restock.querySelector('[data-restock-details]');
    if (restock.hidden && details) details.open = false;

    const variantTitle = variant?.title && variant.title !== 'Default Title' ? ` - ${variant.title}` : '';
    const body = restock.querySelector('[data-restock-body]');
    if (body) body.value = `Please notify me when ${restock.dataset.productTitle}${variantTitle} becomes available.`;
    const variantId = restock.querySelector('[data-restock-variant]');
    if (variantId) variantId.value = variant ? String(variant.id) : '';

    const form = restock.closest('form');
    const returnTo = form?.querySelector('input[name="return_to"]');
    if (returnTo && variant) {
      const url = new URL(restock.dataset.productUrl, window.location.href);
      url.searchParams.set('variant', variant.id);
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      url.hash = form.id;
      returnTo.value = `${url.pathname}${url.search}${url.hash}`;
    }
  };

  const initProducts = (root = document) => {
    root.querySelectorAll('[data-product-section]:not([data-product-ready])').forEach((section) => {
      section.dataset.productReady = 'true';
      const json = section.querySelector('[data-variant-json]');
      const select = section.querySelector('[data-variant-select]');
      const price = section.querySelector('[data-product-price]');
      const comparePrice = section.querySelector('[data-product-compare-price]');
      const submit = section.querySelector('[data-add-to-cart]');
      const dynamicCheckout = section.querySelector('.shopify-payment-button');
      const variantInput = section.querySelector('[data-product-variant-input]');
      const quantity = section.querySelector('[data-quantity-input]');
      const quantityInput = section.querySelector('[data-product-quantity-input]');
      const sellingPlans = section.querySelector('[data-selling-plans]');
      const requiresSellingPlan = sellingPlans?.dataset.requiresSellingPlan === 'true';
      const mobileBar = section.querySelector('[data-mobile-purchase-bar]');
      const mobilePrice = section.querySelector('[data-mobile-purchase-price]');
      const mobileTrigger = section.querySelector('[data-mobile-purchase-trigger]');
      const mobileViewport = window.matchMedia('(max-width: 800px)');
      let purchaseButtonVisible = false;
      let unloaded = false;
      const syncMobileBar = () => {
        if (mobileBar) mobileBar.hidden = unloaded || !mobileViewport.matches || !submit || submit.disabled || purchaseButtonVisible;
      };
      const purchaseObserver = mobileBar && submit ? new IntersectionObserver(([entry]) => {
        purchaseButtonVisible = entry.isIntersecting && entry.intersectionRatio >= 0.95;
        syncMobileBar();
      }, { threshold: [0, 0.95, 1] }) : null;
      purchaseObserver?.observe(submit);
      mobileViewport.addEventListener('change', syncMobileBar);
      mobileTrigger?.addEventListener('click', () => {
        if (!submit || submit.disabled || unloaded) return;
        const target = select || sellingPlans?.querySelector('input:checked:not(:disabled)') || quantity || submit;
        target.focus({ preventScroll: true });
        target.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'center',
        });
      });
      const unloadProduct = (event) => {
        if (event.target !== section && !event.target.contains(section)) return;
        unloaded = true;
        syncMobileBar();
        purchaseObserver?.disconnect();
        mobileViewport.removeEventListener('change', syncMobileBar);
        document.removeEventListener('shopify:section:unload', unloadProduct);
      };
      document.addEventListener('shopify:section:unload', unloadProduct);
      let variants = [];
      try { variants = JSON.parse(json?.textContent || '[]'); } catch { variants = []; }

      const setPrice = (current, compare = '') => {
        if (price && current) price.textContent = current;
        if (mobilePrice && current) mobilePrice.textContent = current;
        if (comparePrice) {
          comparePrice.textContent = compare;
          comparePrice.hidden = !compare;
        }
      };

      const syncSellingPlans = (variantId, option) => {
        if (!sellingPlans) {
          setPrice(option?.dataset.price, option?.dataset.comparePrice);
          return true;
        }
        const groups = [...sellingPlans.querySelectorAll('[data-selling-plan-options]')];
        const previousValue = sellingPlans.querySelector('input[name="selling_plan"]:checked:not(:disabled)')?.value;
        const activeGroup = groups.find((group) => group.dataset.variantId === String(variantId));
        groups.forEach((group) => {
          const active = group === activeGroup;
          group.hidden = !active;
          group.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
            input.disabled = !active;
            if (!active) input.checked = false;
          });
        });
        if (!activeGroup) {
          setPrice(option?.dataset.price, option?.dataset.comparePrice);
          return !requiresSellingPlan;
        }
        const inputs = [...activeGroup.querySelectorAll('input[name="selling_plan"]')];
        const selected = inputs.find((input) => input.value === previousValue)
          || inputs.find((input) => input.defaultChecked)
          || inputs[0];
        if (selected) selected.checked = true;
        setPrice(
          selected?.dataset.sellingPlanPrice || option?.dataset.price,
          selected?.dataset.sellingPlanComparePrice || option?.dataset.comparePrice,
        );
        return !requiresSellingPlan || Boolean(selected?.value);
      };

      const syncAvailability = (variant, planAvailable = true) => {
        syncProductRestock(section, variant);
        const available = Boolean(variant?.available) && planAvailable;
        if (submit) {
          submit.disabled = !available;
          submit.textContent = available ? submit.dataset.addLabel : submit.dataset.soldOutLabel;
        }
        if (dynamicCheckout) dynamicCheckout.hidden = !available;
        syncMobileBar();
      };

      const syncQuantity = () => {
        if (!quantity) return;
        quantity.value = String(Math.max(1, Number(quantity.value || 1)));
        if (quantityInput && quantityInput !== quantity) quantityInput.value = quantity.value;
      };

      select?.addEventListener('change', () => {
        const variant = variants.find((item) => String(item.id) === select.value);
        const option = select.selectedOptions[0];
        if (!variant || !option) return;
        if (variantInput && variantInput !== select) {
          variantInput.value = String(variant.id);
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const planAvailable = syncSellingPlans(variant.id, option);
        syncAvailability(variant, planAvailable);
        const mediaId = option.dataset.featuredMediaId;
        if (mediaId) {
          const media = section.querySelector(`[data-media-id="${mediaId}"]`);
          media?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'nearest',
          });
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      });

      sellingPlans?.addEventListener('change', (event) => {
        const plan = event.target.closest?.('input[name="selling_plan"]');
        if (!plan || plan.disabled) return;
        const variant = variants.find((item) => String(item.id) === String(variantInput?.value));
        setPrice(plan.dataset.sellingPlanPrice, plan.dataset.sellingPlanComparePrice);
        syncAvailability(variant, !requiresSellingPlan || Boolean(plan.value));
      });

      section.querySelectorAll('[data-quantity-change]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!quantity) return;
          quantity.value = String(Math.max(1, Number(quantity.value || 1) + Number(button.dataset.quantityChange)));
          syncQuantity();
        });
      });
      quantity?.addEventListener('change', syncQuantity);
      const currentVariant = variants.find((item) => String(item.id) === variantInput?.value);
      const currentOption = select?.selectedOptions[0];
      const currentPlanAvailable = syncSellingPlans(currentVariant?.id, currentOption);
      syncAvailability(currentVariant, currentPlanAvailable);
      syncQuantity();
    });
  };

  const initRecentlyViewed = (root = document) => {
    root.querySelectorAll('[data-recently-viewed]:not([data-recently-viewed-ready])').forEach((section) => {
      section.dataset.recentlyViewedReady = 'true';
      const currentHandle = section.dataset.productHandle;
      const grid = section.querySelector('[data-recently-viewed-grid]');
      if (!grid || !currentHandle) return;
      const storageKey = 'bolingo-recently-viewed';
      let handles = [];
      try {
        const stored = JSON.parse(safeStorage.get(storageKey) || '[]');
        if (Array.isArray(stored)) handles = stored.filter((handle) => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(handle));
      } catch { handles = []; }
      handles = [currentHandle, ...handles.filter((handle) => handle !== currentHandle)].slice(0, 8);
      safeStorage.set(storageKey, JSON.stringify(handles));
      const previousHandles = handles.slice(1, 5);
      if (!previousHandles.length) return;

      Promise.all(previousHandles.map((handle) => fetch(`/products/${encodeURIComponent(handle)}.js`).then((response) => response.ok ? response.json() : null).catch(() => null)))
        .then((products) => {
          grid.querySelector('[data-recently-viewed-placeholder]')?.remove();
          products.filter(Boolean).forEach((product) => {
            const card = document.createElement('article');
            card.className = 'product-card';
            const link = document.createElement('a');
            link.className = 'product-card__image';
            link.href = product.url || `/products/${encodeURIComponent(product.handle)}`;
            link.setAttribute('aria-label', `View ${product.title || 'product'}`);
            if (product.featured_image) {
              const image = document.createElement('img');
              image.src = product.featured_image;
              image.alt = product.title || '';
              image.loading = 'lazy';
              link.append(image);
            }
            const content = document.createElement('div');
            content.className = 'product-card__content';
            const title = document.createElement('h3');
            const titleLink = document.createElement('a');
            titleLink.href = link.href;
            titleLink.className = '';
            titleLink.textContent = product.title || 'View product';
            title.append(titleLink);
            const price = document.createElement('p');
            price.className = 'product-price';
            const currency = window.Shopify?.currency?.active || 'USD';
            const formatMoney = (amount) => {
              try {
                return new Intl.NumberFormat(document.documentElement.lang || 'en', {
                  style: 'currency',
                  currency,
                }).format(Number(amount || 0) / 100);
              } catch {
                return `${(Number(amount || 0) / 100).toFixed(2)} ${currency}`;
              }
            };
            if (product.price_varies) {
              const from = document.createElement('span');
              from.textContent = 'From ';
              price.append(from);
            }
            const currentPrice = document.createElement('span');
            currentPrice.textContent = formatMoney(product.price);
            price.append(currentPrice);
            if (Number(product.compare_at_price) > Number(product.price)) {
              const comparePrice = document.createElement('s');
              comparePrice.textContent = formatMoney(product.compare_at_price);
              price.append(' ', comparePrice);
            }
            content.append(title, price);
            card.append(link, content);
            grid.append(card);
          });
          if (grid.children.length) section.hidden = false;
        });
    });
  };

  const initProductRecommendations = (root = document) => {
    root.querySelectorAll('[data-product-recommendations-url]:not([data-product-recommendations-ready])').forEach((section) => {
      section.dataset.productRecommendationsReady = 'true';
      if (section.querySelector('.product-card')) {
        section.hidden = false;
        return;
      }
      const url = section.dataset.productRecommendationsUrl;
      if (!url) return;

      fetch(url)
        .then((response) => response.ok ? response.text() : '')
        .then((html) => {
          if (!html) return;
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const recommendations = documentFragment.querySelector('[data-product-recommendations]');
          if (!recommendations?.querySelector('.product-card')) return;
          section.innerHTML = recommendations.innerHTML;
          section.hidden = false;
        })
        .catch(() => { /* Recommendations are optional and must not block product purchase. */ });
    });
  };

  const initFacets = (root = document) => {
    root.querySelectorAll('[data-facets-form]:not([data-facets-ready])').forEach((form) => {
      form.dataset.facetsReady = 'true';
      form.querySelector('select[name="sort_by"]')?.addEventListener('change', () => form.requestSubmit());
    });
  };

  const initTheme = (root = document) => {
    initHeader(root);
    initNewsletter(root);
    initProducts(root);
    initProductRecommendations(root);
    initRecentlyViewed(root);
    initFacets(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initTheme());
  else initTheme();

  document.addEventListener('shopify:section:load', (event) => initTheme(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    const dialog = event.target.querySelector?.('[data-newsletter-dialog]');
    if (dialog && !dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
  });
})();
